import { type SalesRegisterRow } from '@utazzo/shared';
import { UzInput, UzSelect } from '@utazzo/ui';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { FyRangeControls, useFyRange } from '../components/FyRangeControls';
import { formatDateDMY, formatInrPaise } from '../invoices.helpers';
import { useSalesRegister } from '../use-invoices';

import {
  ReportListScreen,
  type ReportColumn,
} from '@/components/report-list-screen/ReportListScreen';

/**
 * Booking Detail (Sales register) - report #1 of the MYTAdmin reporting rebuild,
 * built on the shared ReportListScreen shell. One row per active invoice with
 * sale / cost / margin / GST / received / due / clear-days, filterable by date
 * range, corporate flag and free text (agent / branch rollup to follow).
 */

const CORPORATE_OPTIONS = [
  { value: 'ALL', label: 'All (retail + corporate)' },
  { value: 'CORPORATE', label: 'Corporate only' },
  { value: 'RETAIL', label: 'Retail only' },
];

const rupees = (paise: string): number => Number(paise) / 100;
const sumPaise = (rows: ReadonlyArray<SalesRegisterRow>, pick: (r: SalesRegisterRow) => string) =>
  formatInrPaise(rows.reduce((acc, r) => acc + BigInt(pick(r)), 0n).toString());

/** A right-aligned money column: raw rupees for export, formatted for display. */
const money = (
  header: string,
  pick: (r: SalesRegisterRow) => string,
  withTotal = true,
): ReportColumn<SalesRegisterRow> => ({
  header,
  align: 'right',
  value: (r) => rupees(pick(r)),
  render: (r) => formatInrPaise(pick(r)),
  ...(withTotal ? { total: (rows) => sumPaise(rows, pick) } : {}),
});

export const SalesRegisterPage = (): React.JSX.Element => {
  const fyState = useFyRange();
  const { from, to } = fyState;
  const [corporate, setCorporate] = useState<'ALL' | 'CORPORATE' | 'RETAIL'>('ALL');
  const [q, setQ] = useState('');

  const query = {
    from,
    to,
    corporate,
    ...(q.trim() ? { search: q.trim() } : {}),
  };
  const reportQuery = useSalesRegister(query);
  const rows = reportQuery.data?.rows ?? [];

  const columns: ReportColumn<SalesRegisterRow>[] = [
    {
      header: 'Invoice No.',
      value: (r) => r.invoiceNumber,
      render: (r) => (
        <Link to={`/invoices/${r.invoiceId}`} className="text-primary hover:underline">
          {r.invoiceNumber}
        </Link>
      ),
    },
    { header: 'Date', value: (r) => formatDateDMY(r.invoiceDate ?? '') },
    { header: 'Customer', value: (r) => r.customerName ?? '' },
    { header: 'Agent', value: (r) => r.agentName ?? '' },
    { header: 'Branch', value: (r) => r.branchName ?? '' },
    { header: 'Type', value: (r) => (r.isCorporate ? 'Corporate' : 'Retail') },
    { header: 'Product', value: (r) => r.productType ?? '' },
    money('Gross Sale', (r) => r.grossSalePaise),
    money('Supplier Cost', (r) => r.supplierCostPaise),
    money('Margin', (r) => r.marginPaise),
    money('GST', (r) => r.gstPaise),
    money('Grand Total', (r) => r.grandTotalPaise),
    money('Received', (r) => r.receivedPaise),
    money('Due', (r) => r.duePaise),
    {
      header: 'Clear Days',
      align: 'right',
      value: (r) => r.clearDays ?? '',
      render: (r) => (r.clearDays === null ? '-' : String(r.clearDays)),
    },
  ];

  return (
    <ReportListScreen
      title="Booking Detail (Sales Register)"
      description="One row per invoice: sale, supplier cost, margin, GST, received and due with clear-days."
      columns={columns}
      rows={rows}
      getRowKey={(r) => r.invoiceId}
      loading={reportQuery.isLoading}
      error={reportQuery.isError}
      emptyMessage="No invoices match these filters."
      exportName="sales-register"
      search={
        <UzInput
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search invoice / customer / agent..."
          containerClassName="max-w-md"
        />
      }
      filters={
        <>
          <FyRangeControls state={fyState} />
          <UzSelect
            label="Customer type"
            value={corporate}
            onValueChange={(v) => setCorporate(v as 'ALL' | 'CORPORATE' | 'RETAIL')}
            options={CORPORATE_OPTIONS}
            containerClassName="w-56"
          />
        </>
      }
    />
  );
};
